// Interruptor del aviso de oferta por mail (PP-68): lee la preferencia de la
// cuenta una vez por sesion y la guarda al tocarlo. Mientras guarda, el
// interruptor muestra el valor pedido; si falla, vuelve al ultimo confirmado.
import { useCallback, useEffect, useRef, useState } from "react";
import type { PortalSession } from "../auth/session.js";
import type { NotificationsPort } from "./port.js";

export type AvisoDeOfertas =
  | { readonly estado: "cargando" }
  | { readonly estado: "no_disponible" }
  | { readonly estado: "listo"; readonly activo: boolean; readonly guardando: boolean; readonly fallo: boolean };

export function useOfferMail(port: NotificationsPort, session: PortalSession): { aviso: AvisoDeOfertas; cambiar: (activo: boolean) => void } {
  const [aviso, setAviso] = useState<AvisoDeOfertas>({ estado: "cargando" });

  useEffect(() => {
    let vigente = true;
    setAviso({ estado: "cargando" });
    port
      .read(session)
      .then((value) => {
        if (vigente) setAviso({ estado: "listo", activo: value.offerMail, guardando: false, fallo: false });
      })
      .catch(() => {
        if (vigente) setAviso({ estado: "no_disponible" });
      });
    return () => {
      vigente = false;
    };
  }, [port, session]);

  // El ultimo estado pintado, para decidir fuera del updater de setState (que
  // React puede llamar dos veces): un toque es un solo PUT.
  const actual = useRef(aviso);
  actual.current = aviso;

  const cambiar = useCallback(
    (activo: boolean) => {
      const previo = actual.current;
      if (previo.estado !== "listo" || previo.guardando) return;
      const confirmado = previo.activo;
      const enCurso: AvisoDeOfertas = { estado: "listo", activo, guardando: true, fallo: false };
      actual.current = enCurso;
      setAviso(enCurso);
      port
        .save(session, { offerMail: activo })
        .then((value) => {
          setAviso({ estado: "listo", activo: value.offerMail, guardando: false, fallo: false });
        })
        .catch(() => {
          setAviso({ estado: "listo", activo: confirmado, guardando: false, fallo: true });
        });
    },
    [port, session],
  );

  return { aviso, cambiar };
}
